import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningIcon      from '@mui/icons-material/Warning';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';

import { getLoggedInArea, getAreaDisplayName, isAdminUser } from '../../utils/authService';

// ─── constants ───────────────────────────────────────────────────────────────

// Same backend that VTSReportsView uses for /api/reports/* endpoints
const VTS_API_BASE = 'https://bbstate.cclai.in';


/**
 * Maps live-event alertType strings → the backend :type param.
 * Live events come in with lowercase hyphenated names from the WebSocket.
 */
const LIVE_TYPE_TO_API = {
  'off-route': 'offroute',
  'off-area':  'offarea',   // own endpoint — same tc_offroute table, area-fence rows
  'tamper':    'tamper',
  'overspeed': 'overspeed',
  'stoppage':  'stoppage',
};

/**
 * Maps DB report.reportType → the backend :type param.
 * DB events have uppercase reportType like "OFFROUTE", "OFFAREA", "TAMPER", …
 */
const REPORT_TYPE_TO_API = {
  'OFFROUTE':  'offroute',
  'OFFAREA':   'offarea',
  'TAMPER':    'tamper',
  'STOPPAGE':  'stoppage',
  'OVERSPEED': 'overspeed',
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The backend stores the remark date already in IST.
 * Just append the IST label — no timezone conversion needed.
 */
const formatRemarkForDisplay = (remark) => {
  if (!remark) return remark;
  return remark.replace(
    /Date: (\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/,
    (_, dateStr) => `Date: ${dateStr} IST`
  );
};

/**
 * Return the current time formatted as IST for the remark preview hint.
 */
const nowISTString = () => {
  return new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }) + ' IST';
};

// ─────────────────────────────────────────────────────────────────────────────

const VehicleAlertMapModal = ({ open, onClose, event, TransitionProps, liveClosedCache = {}, onLiveEventClosed }) => {
  const mapContainer = useRef(null);
  const [mapLoading,  setMapLoading]  = useState(true);
  const [mapError,    setMapError]    = useState(null);
  const [geofenceData, setGeofenceData] = useState(null);

  // ── remark / close state ──────────────────────────────────────────────────
  const [remarks,       setRemarks]       = useState('');
  const [isClosing,     setIsClosing]     = useState(false);
  const [submitError,   setSubmitError]   = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Existing remark (populated from DB-report event data or from a 409 response)
  const [existingRemark, setExistingRemark] = useState(null);

  // Override-confirmation dialog
  const [overrideDialogOpen,  setOverrideDialogOpen]  = useState(false);
  const [pendingForcePayload, setPendingForcePayload] = useState(null);

  // ── event kind detection ──────────────────────────────────────────────────
  // DB report: has numeric `id` + a string `reportType` like "OFFROUTE"
  // Live event: has `vehicleNumber` + `alertType`; no `reportType`
  const isDBReport  = !!(event?.reportType && typeof event?.id === 'number');
  const isLiveEvent = !isDBReport && !!event?.vehicleNumber;

  // Backend :type param resolved from whichever event kind we have
  const apiType = useMemo(() => {
    if (!event) return null;
    if (isDBReport)  return REPORT_TYPE_TO_API[event.reportType]  ?? null;
    return LIVE_TYPE_TO_API[event.alertType] ?? null;
  }, [event, isDBReport]);

  // Logged-in area label used as the "User:" field in the remark string.
  // Admin users get the label "Admin"; area users get their area display name.
  const userLabel = useMemo(() => isAdminUser() ? 'Admin' : (getAreaDisplayName() || 'CCL Dashboard'), []);

  // ── coordinates ───────────────────────────────────────────────────────────
  const coordinates = useMemo(() => {
    if (!event) return null;

    // Live event coordinates
    if (event?.currentLocation) {
      const lat = parseFloat(event.currentLocation.lat);
      const lng = parseFloat(event.currentLocation.lng);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }

    // DB report coordinates (latitude / longitude fields)
    if (event?.latitude != null && event?.longitude != null) {
      const lat = parseFloat(event.latitude);
      const lng = parseFloat(event.longitude);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }

    // Fallback: comma-separated string
    if (event?.coordinates && typeof event.coordinates === 'string') {
      const parts = event.coordinates.split(',').map(c => parseFloat(c.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]))
        return { lat: parts[0], lng: parts[1] };
    }

    return null;
  }, [
    event?.currentLocation?.lat, event?.currentLocation?.lng,
    event?.latitude, event?.longitude, event?.coordinates,
  ]);

  // ── stable key for live events (vehicleNumber + timeStamp) ──────────────
  const liveEventKey = !isDBReport && event?.vehicleNumber && event?.timeStamp
    ? `${event.vehicleNumber}_${event.timeStamp}`
    : null;

  // ── initialise UI state on each open ─────────────────────────────────────
  useEffect(() => {
    if (!open || !event) return;

    if (isDBReport) {
      // DB report: remark already on the event object
      const existing = event.remarks ?? event.remark ?? null;
      setExistingRemark(existing || null);
    } else {
      // Live event: check the in-memory closed-events cache first
      const cached = liveEventKey ? (liveClosedCache[liveEventKey] ?? null) : null;
      setExistingRemark(cached);
    }

    // Reset transient UI every time the modal opens
    setRemarks('');
    setSubmitError(null);
    setSubmitSuccess(false);
    setOverrideDialogOpen(false);
    setPendingForcePayload(null);
  }, [open, event?.id, event?.vehicleNumber, event?.timeStamp, isDBReport, liveEventKey]);

  // ── geofence fetch ────────────────────────────────────────────────────────
  // Only relevant for offroute / offarea / tamper — stoppage and overspeed
  // don't carry a single geofenceId so we skip them entirely.
  //
  // geofenceId can arrive as: a number, a numeric string, or an array like
  // [440, 289] (tamper events with multiple zones). We resolve to a single
  // integer — the first element of an array, or the value itself. If it
  // resolves to a falsy / non-numeric value we skip the fetch entirely.
  const GEOFENCE_ALERT_TYPES = ['off-route', 'off-area', 'tamper', 'OFFROUTE', 'OFFAREA', 'TAMPER'];
  const alertTypeForCheck = event?.alertType || event?.reportType || '';

  const resolvedGeofenceId = useMemo(() => {
    const raw = event?.geofenceId;
    if (!raw && raw !== 0) return null;
    if (Array.isArray(raw)) return raw.length > 0 ? Number(raw[0]) : null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [event?.geofenceId]);

  const shouldFetchGeofence =
    !!resolvedGeofenceId &&
    GEOFENCE_ALERT_TYPES.some(t => alertTypeForCheck.toUpperCase().includes(t.toUpperCase()));

  useEffect(() => {
    const fetchGeofence = async () => {
      try {
        const response = await fetch(
          `${VTS_API_BASE}/api/geofences/${resolvedGeofenceId}?includeArea=1`
        );
        if (!response.ok) throw new Error('Failed to fetch geofence');
        const json = await response.json();
        setGeofenceData(json.data ?? null);
      } catch {
        setGeofenceData(null);
      }
    };
    if (open && shouldFetchGeofence) fetchGeofence();
    else setGeofenceData(null);
  }, [open, resolvedGeofenceId, shouldFetchGeofence]);

  // ── map loading ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !coordinates) return;
    setMapLoading(true);
    setMapError(null);
    const timer = setTimeout(() => setMapLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [open, coordinates]);

  // ─────────────────────────────────────────────────────────────────────────
  // Core close-event handler
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Submit a remark (close the alert).
   *
   * @param {boolean} force          – true when the user has confirmed override
   * @param {object|null} forceBody  – pre-built body to re-send with force=true (override path)
   */
  const doCloseEvent = useCallback(async (force = false, forceBody = null) => {
    const commentText = forceBody?.comment || remarks.trim();

    if (!commentText) {
      setSubmitError('Please enter a comment before closing the event.');
      return;
    }
    if (!apiType) {
      setSubmitError('Unknown alert type — cannot determine the correct database table.');
      return;
    }

    setIsClosing(true);
    setSubmitError(null);

    try {
      let url, method, body;

      if (isDBReport) {
        // ── DB report: close by primary key ──────────────────────────────────
        url    = `${VTS_API_BASE}/api/reports/${apiType}/${event.id}/remark`;
        method = 'PATCH';
        body   = forceBody ?? { comment: commentText, user: userLabel, force };
      } else {
        // ── Live event: close by vehicleNumber + timestamp ────────────────────
        // event.timeStamp from WebSocket is already UTC/GMT
        // (e.g. "2026-05-18 06:32:38" UTC, displayed as "12:02:38 IST").
        // DB also stores UTC → send as-is, no conversion needed.
        url    = `${VTS_API_BASE}/api/reports/${apiType}/close-by-vehicle`;
        method = 'POST';
        body   = forceBody ?? {
          vehicleNumber: event.vehicleNumber,
          timestamp:     event.timeStamp,
          comment:       commentText,
          user:          userLabel,
          force,
        };
      }

      // Strip internal tracking fields before sending over the wire
      const { _retryCount: _rc, ...sendBody } = body;
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(sendBody),
      });

      const data = await response.json();

      // ── existing remark — ask user to confirm override ──────────────────
      if (response.status === 409 && data.hasExistingRemark && !force) {
        setExistingRemark(data.existingRemark);
        // Store the payload we'll re-send with force=true if the user confirms
        setPendingForcePayload({ ...body, force: true });
        setOverrideDialogOpen(true);
        return;
      }

      // ── live event not yet in DB — auto-retry up to 3 times ─────────────
      if (response.status === 404 && data.notFound) {
        const MAX_RETRIES = 3;
        // _retryCount is tracked in forceBody but stripped before sending
        const retryAttempt = (forceBody?._retryCount || 0);

        if (!isDBReport && retryAttempt < MAX_RETRIES) {
          setSubmitError(
            `Event is being recorded… retrying in 3 s (attempt ${retryAttempt + 1}/${MAX_RETRIES}).`
          );
          // Build clean retry body (no _retryCount on the wire) + hidden counter
          const { _retryCount: _dropped, ...cleanBody } = (forceBody ?? body);
          const retryMeta = { ...cleanBody, _retryCount: retryAttempt + 1 };
          setTimeout(() => doCloseEvent(force, retryMeta), 3000);
          return;
        }

        // All retries exhausted
        setSubmitError(
          'This event has not been recorded in the database yet. ' +
          'Please wait a few seconds and try again manually.'
        );
        return;
      }

      // ── generic server error ─────────────────────────────────────────────
      if (!response.ok) {
        setSubmitError(data.error || 'Failed to save remark. Please try again.');
        return;
      }

      // ── success ──────────────────────────────────────────────────────────
      setSubmitSuccess(true);
      setExistingRemark(data.remark); // reflect saved remark in UI

      // Cache closed live events in parent so other modals / reopens pick it up
      if (!isDBReport && liveEventKey && onLiveEventClosed) {
        onLiveEventClosed(liveEventKey, data.remark);
      }

      // After 2 s transition from "saved" banner → override mode so operator
      // can correct a mistake without reopening the modal.
      setTimeout(() => {
        setSubmitSuccess(false);
        setRemarks('');   // clear so the textarea is ready for a new comment
      }, 2000);

    } catch (err) {
      setSubmitError(`Network error: ${err.message}`);
    } finally {
      setIsClosing(false);
    }
  }, [remarks, apiType, isDBReport, event, userLabel, onClose]);

  const handleCloseEvent   = () => doCloseEvent(false);
  const handleOverrideConfirm = useCallback(async () => {
    setOverrideDialogOpen(false);
    if (!pendingForcePayload) return;
    await doCloseEvent(true, pendingForcePayload);
  }, [doCloseEvent, pendingForcePayload]);

  // ─────────────────────────────────────────────────────────────────────────
  // Display helpers (unchanged from original)
  // ─────────────────────────────────────────────────────────────────────────

  const getAlertTypeText = () => {
    const t = event?.alertType || event?.reportType || '';
    switch (t.toLowerCase().replace('_', '').replace(' ', '')) {
      case 'tamper':    return 'Tamper Alert';
      case 'offarea':
      case 'off-area':  return 'Off-Area Alert';
      case 'overspeed': return 'Over Speed Alert';
      case 'stoppage':
      case 'unauthorizedstoppage': return 'Unauthorized Stoppage';
      case 'offroute':
      case 'off-route':
      default:          return 'Off-Route Alert';
    }
  };

  const getAlertEmoji = () => {
    const t = event?.alertType || '';
    switch (t) {
      case 'tamper':    return '⚠️';
      case 'stoppage':  return '🛑';
      case 'overspeed': return '🚨';
      case 'off-area':  return '📍';
      default:          return '📍';
    }
  };

  const getAlertColor = () => {
    const t = event?.alertType || event?.reportType?.toLowerCase() || '';
    if (t.includes('tamper'))    return '#DC143C';
    if (t.includes('off-area') || t.includes('offarea')) return '#FF1744';
    if (t.includes('overspeed')) return '#E74C3C';
    if (t.includes('stoppage'))  return '#F39C12';
    return '#FF6B35';
  };

  const getAlertIcon = () => {
    const t = event?.alertType || '';
    if (t === 'tamper' || t === 'stoppage') return <WarningIcon />;
    return <DirectionsCarIcon />;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Leaflet map HTML (unchanged from original)
  // ─────────────────────────────────────────────────────────────────────────

  const getLeafletMapHtml = () => {
    if (!coordinates) return null;

    const { lat, lng }   = coordinates;
    const markerColor    = getAlertColor();
    const alertTypeText  = getAlertTypeText();

    let geofenceCoords   = [];
    let geofenceType     = '';
    let geofenceColor    = '#ffa500';
    let geofenceName     = '';
    let polylineDistance = 100;

    if (geofenceData) {
      // `area` is GeoJSON from our bbstate cache — coordinates are [lon, lat]
      // (GeoJSON standard). Leaflet needs [lat, lon], so we flip in the map JS below.
      if (geofenceData.area?.coordinates) {
        const coords = geofenceData.area.coordinates;
        if (Array.isArray(coords) && coords.length > 0) {
          // Polygon → coordinates[0] is the outer ring [[lon,lat],…]
          // LineString → coordinates is [[lon,lat],…] directly
          geofenceCoords = (Array.isArray(coords[0]) && Array.isArray(coords[0][0]))
            ? coords[0]
            : coords;
        }
      }
      geofenceType     = geofenceData.geotype || '';
      geofenceName     = geofenceData.name    || '';
      // color and polylineDistance are top-level fields in our cache response
      polylineDistance = geofenceData.polylineDistance ?? 100;
      geofenceColor    = geofenceData.color             || '#FFA500';
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Vehicle Alert Location</title>
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
        <style>
          body { margin: 0; padding: 0; }
          #map { height: 100vh; width: 100%; }
          .custom-marker {
            background-color: ${markerColor};
            color: white;
            border: 2px solid white;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }
        </style>
      </head>
      <body>
        <div id="map"></div>
        <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
        <script>
          var rawCoords = ${JSON.stringify(geofenceCoords)};
          var map = L.map('map').setView([${lat}, ${lng}], 13);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors', maxZoom: 19
          }).addTo(map);

          var customIcon = L.divIcon({
            html: '${event?.alertType === 'tamper' ? '⚠' : '🚛'}',
            className: 'custom-marker',
            iconSize: [30, 30],
            iconAnchor: [15, 15]
          });

          var marker = L.marker([${lat}, ${lng}], {icon: customIcon}).addTo(map);
          marker.bindPopup(\`
            <div style="text-align:center;font-family:Arial,sans-serif;">
              <strong style="color:${markerColor};">${alertTypeText}</strong><br>
              <strong>Vehicle:</strong> ${event?.vehicleNumber || event?.deviceName || 'N/A'}<br>
              <strong>Time:</strong> ${event?.readableTime || event?.timestamp || 'N/A'}<br>
              <strong>Coords:</strong> ${lat.toFixed(6)}, ${lng.toFixed(6)}
            </div>
          \`).openPopup();


          if (rawCoords && rawCoords.length > 0) {
            try {
              if ('${geofenceType}' === 'P') {
                // GeoJSON coords are [lon, lat] — flip to [lat, lon] for Leaflet
                var pathCoords = rawCoords.map(function(c) { return [c[1], c[0]]; });
                var polyline = L.polyline(pathCoords, { color: '${geofenceColor}', weight: 5, opacity: 0.9 }).addTo(map);
                polyline.bindPopup('<strong>Route:</strong> ${geofenceName}');
                var bufferDeg = ${polylineDistance} / 111320;
                var leftBuf = [], rightBuf = [];
                for (var i = 0; i < pathCoords.length; i++) {
                  var pt = pathCoords[i], np = pathCoords[i+1];
                  var angle = 0;
                  if (np) angle = Math.atan2(np[1]-pt[1], np[0]-pt[0]);
                  else if (i > 0) { var pp = pathCoords[i-1]; angle = Math.atan2(pt[1]-pp[1], pt[0]-pp[0]); }
                  var perp = angle + Math.PI/2;
                  leftBuf.push([pt[0]+Math.sin(perp)*bufferDeg, pt[1]+Math.cos(perp)*bufferDeg]);
                  rightBuf.push([pt[0]-Math.sin(perp)*bufferDeg, pt[1]-Math.cos(perp)*bufferDeg]);
                }
                L.polygon(leftBuf.concat(rightBuf.reverse()), {
                  color:'${geofenceColor}', fillColor:'${geofenceColor}', fillOpacity:0.15, weight:1, opacity:0.5
                }).addTo(map);
                var bounds = L.latLngBounds(pathCoords);
                bounds.extend([${lat}, ${lng}]);
                map.fitBounds(bounds, {padding:[50,50]});
              } else {
                // GeoJSON coords are [lon, lat] — flip to [lat, lon] for Leaflet
                var polygonCoords = rawCoords.map(function(c) { return [c[1], c[0]]; });
                var polygon = L.polygon(polygonCoords, {
                  color:'${geofenceColor}', fillColor:'${geofenceColor}', fillOpacity:0.2, weight:3, opacity:0.8
                }).addTo(map);
                polygon.bindPopup('<strong>Geofence:</strong> ${geofenceName}');
                var bounds = L.latLngBounds(polygonCoords);
                bounds.extend([${lat}, ${lng}]);
                map.fitBounds(bounds, {padding:[50,50]});
              }
            } catch(err) {
              map.setView([${lat}, ${lng}], 15);
            }
          } else {
            map.setView([${lat}, ${lng}], 15);
          }
        </script>
      </body>
      </html>
    `;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Guard
  // ─────────────────────────────────────────────────────────────────────────

  if (!event) return null;

  const alertColor = getAlertColor();

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Main alert modal ─────────────────────────────────────────────── */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        TransitionProps={TransitionProps}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: {
            backgroundColor: '#1a2332',
            color: 'white',
            height: '88vh',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '14px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          },
        }}
      >
        {/* ── Header bar ──────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5,
          background: `linear-gradient(135deg, #1a2332 0%, #243447 100%)`,
          borderBottom: `2px solid ${alertColor}`,
          flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              bgcolor: `${alertColor}20`, border: `1px solid ${alertColor}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              {getAlertEmoji()}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                  {event.vehicleNumber || event.deviceName}
                </Typography>
                <Chip
                  label={getAlertTypeText()}
                  size="small"
                  sx={{ bgcolor: alertColor, color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                />
                {(existingRemark && !submitSuccess) && (
                  <Chip label="CLOSED" size="small"
                    sx={{ bgcolor: 'rgba(39,174,96,0.2)', color: '#27ae60', border: '1px solid rgba(39,174,96,0.4)', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                )}
                {submitSuccess && (
                  <Chip label="✓ SAVED" size="small"
                    sx={{ bgcolor: 'rgba(39,174,96,0.2)', color: '#27ae60', border: '1px solid rgba(39,174,96,0.4)', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                {event.readableTime || event.timeStamp || event.timestamp || ''}
                {event.areaDisplay ? `  ·  ${event.areaDisplay}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' }, borderRadius: '8px' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Body: left panel + map ───────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* LEFT — details + close alert */}
          <Box sx={{
            width: 360, flexShrink: 0,
            display: 'flex', flexDirection: 'column',
            bgcolor: '#1a2332',
            borderRight: '1px solid rgba(255,255,255,0.07)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
          }}>

            {/* Alert info grid */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography variant="caption" sx={{ color: alertColor, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Alert Details
              </Typography>

              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Vehicle */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', pt: 0.2 }}>Vehicle</Typography>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 700, textAlign: 'right' }}>
                    {event.vehicleNumber || event.deviceName || '—'}
                  </Typography>
                </Box>

                {/* Transporter */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', pt: 0.2, flexShrink: 0 }}>Transporter</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', textAlign: 'right', fontSize: '0.8rem' }}>
                    {event.vehicleTransporter || event.transporter || '—'}
                  </Typography>
                </Box>

                {/* Time */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>Alert Time</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem' }}>
                    {event.readableTime || event.timeStamp || event.timestamp || '—'}
                  </Typography>
                </Box>

                {/* Coordinates */}
                {coordinates && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>Location</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace', fontSize: '0.68rem' }}>
                      {coordinates.lat.toFixed(5)}, {coordinates.lng.toFixed(5)}
                    </Typography>
                  </Box>
                )}

                {/* Speed (overspeed only) */}
                {event.alertType === 'overspeed' && event.actualSpeed != null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>Speed</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#F39C12', fontWeight: 700 }}>{event.actualSpeed} km/h</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}>/ limit {event.allowedSpeed} km/h</Typography>
                    </Box>
                  </Box>
                )}

                {/* Sub-type (tamper) */}
                {event.alertSubType && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>Sub-Type</Typography>
                    <Chip label={event.alertSubType} size="small"
                      sx={{ bgcolor: 'rgba(255,193,7,0.15)', color: '#ffc107', fontSize: '0.65rem', height: 18 }} />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Allocated geofence */}
            {event.allocatedGeofence && event.allocatedGeofence.length > 0 && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Allocated Geofence
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {event.allocatedGeofence.map((fence, i) => (
                    <Chip key={i} label={fence} size="small"
                      sx={{ bgcolor: 'rgba(76,175,80,0.12)', color: '#4caf50', border: '1px solid rgba(76,175,80,0.25)', fontSize: '0.68rem' }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Geofence boundary — show for offroute/offarea/tamper regardless of
                whether an ID is set, so null IDs render "No geofence assigned"
                instead of an infinite spinner */}
            {GEOFENCE_ALERT_TYPES.some(t => alertTypeForCheck.toUpperCase().includes(t.toUpperCase())) && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Box sx={{
                  p: 1.5, borderRadius: 2,
                  bgcolor: 'rgba(78,205,196,0.05)',
                  border: '1px solid rgba(78,205,196,0.15)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: '#4ECDC4', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {geofenceData?.geotype === 'P' ? 'Route / Path' : 'Geofence Boundary'}
                    </Typography>
                    {geofenceData?.area && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.2,
                        bgcolor: 'rgba(78,205,196,0.12)', border: '1px solid rgba(78,205,196,0.25)', borderRadius: '20px' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#4ECDC4' }} />
                        <Typography variant="caption" sx={{ color: '#4ECDC4', fontSize: '0.6rem', fontWeight: 600 }}>On map</Typography>
                      </Box>
                    )}
                  </Box>
                  {(geofenceData?.name || event?.geofenceName) ? (
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>
                      {geofenceData?.name || event.geofenceName}
                    </Typography>
                  ) : shouldFetchGeofence && !geofenceData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <CircularProgress size={9} sx={{ color: 'rgba(78,205,196,0.5)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>Loading boundary…</Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                      No geofence assigned
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Existing remark */}
            {existingRemark && (
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: '#27ae60', fontSize: '0.95rem' }} />
                  <Typography variant="caption" sx={{ color: '#27ae60', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Already Closed
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.25)', border: '1px solid rgba(39,174,96,0.2)', borderRadius: 1.5, p: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.7rem' }}>
                    {formatRemarkForDisplay(existingRemark)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.75, display: 'block', fontSize: '0.65rem' }}>
                  Submitting below will override this remark.
                </Typography>
              </Box>
            )}

            {/* Close alert — remark form */}
            <Box sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography variant="caption" sx={{ color: alertColor, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {existingRemark ? 'Override Remark' : 'Close Alert'}
              </Typography>

              {submitSuccess && (
                <Alert severity="success" icon={<CheckCircleIcon />}
                  sx={{ mt: 1.5, mb: 1, bgcolor: 'rgba(39,174,96,0.1)', color: '#27ae60', border: '1px solid rgba(39,174,96,0.25)', '& .MuiAlert-icon': { color: '#27ae60' } }}>
                  Alert closed. Remark saved.
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ mt: 1.5, mb: 1, bgcolor: 'rgba(220,53,69,0.1)', color: '#e74c3c', border: '1px solid rgba(220,53,69,0.25)' }}
                  onClose={() => setSubmitError(null)}>
                  {submitError}
                </Alert>
              )}

              <TextField
                fullWidth multiline rows={3}
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                placeholder={existingRemark ? 'New remark to override existing…' : 'Reason for closing this alert…'}
                disabled={isClosing || submitSuccess}
                variant="outlined"
                sx={{
                  mt: 1.25,
                  '& .MuiOutlinedInput-root': {
                    color: 'white', fontSize: '0.82rem',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
                    '&.Mui-focused fieldset': { borderColor: alertColor },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 },
                }}
              />

              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', mt: 0.75, display: 'block', fontSize: '0.63rem' }}>
                Saved as: User: {userLabel} · {nowISTString()} (approx)
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button onClick={onClose} variant="outlined" size="small" fullWidth
                  sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.15)', borderRadius: '8px', fontSize: '0.75rem',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                  Cancel
                </Button>
                <Button onClick={handleCloseEvent} variant="contained" size="small" fullWidth
                  disabled={isClosing || !remarks.trim() || submitSuccess}
                  startIcon={isClosing ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ bgcolor: alertColor, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700,
                    '&:hover': { bgcolor: alertColor, opacity: 0.88 },
                    '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' } }}>
                  {isClosing ? 'Saving…' : existingRemark ? 'Override' : 'Close Alert'}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* RIGHT — full-height map */}
          <Box sx={{ flex: 1, position: 'relative', bgcolor: '#111820' }}>
            {!coordinates ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Alert severity="warning"
                  sx={{ bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', border: '1px solid rgba(255,152,0,0.3)' }}>
                  No coordinates available for this alert.
                </Alert>
              </Box>
            ) : (
              <>
                {mapLoading && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(17,24,32,0.92)', zIndex: 10 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ color: alertColor, mb: 1.5 }} size={36} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Loading map…</Typography>
                    </Box>
                  </Box>
                )}

                {/* Map iframe — fills the entire right panel */}
                <iframe
                  srcDoc={getLeafletMapHtml()}
                  width="100%" height="100%"
                  style={{ border: 'none', display: 'block' }}
                  title={`${event.alertType || event.reportType} Alert Location`}
                />

                {/* Alert type badge — top left */}
                <Box sx={{
                  position: 'absolute', top: 12, left: 12,
                  bgcolor: alertColor, color: 'white',
                  px: 1.5, py: 0.6, borderRadius: '8px',
                  fontSize: '0.78rem', fontWeight: 700,
                  boxShadow: `0 2px 12px ${alertColor}60`,
                  zIndex: 5, display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                  {getAlertEmoji()} {getAlertTypeText()}
                </Box>

                {/* Geofence badge — top right */}
                {geofenceData && (
                  <Box sx={{
                    position: 'absolute', top: 12, right: 12,
                    bgcolor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
                    color: 'white', px: 1.25, py: 0.6,
                    border: `1px solid ${geofenceData.color || '#ffa500'}50`,
                    borderRadius: '8px', fontSize: '0.72rem', fontWeight: 600,
                    zIndex: 5, display: 'flex', alignItems: 'center', gap: 0.5,
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: geofenceData.color || '#ffa500', flexShrink: 0 }} />
                    {geofenceData.geotype === 'P' ? 'Route' : 'Area'}: {geofenceData.name}
                  </Box>
                )}

                {/* Coordinates — bottom right */}
                <Box sx={{
                  position: 'absolute', bottom: 12, right: 12,
                  bgcolor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                  color: 'rgba(255,255,255,0.7)',
                  px: 1, py: 0.4, borderRadius: '6px',
                  fontSize: '0.68rem', fontFamily: 'monospace', zIndex: 5,
                }}>
                  {coordinates.lat.toFixed(5)}, {coordinates.lng.toFixed(5)}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* ── Override confirmation dialog ────────────────────────────────────── */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: { backgroundColor: '#1e2a38', color: 'white' } }}
      >
        <DialogTitle sx={{ backgroundColor: '#2C3E50', color: 'white', borderBottom: '2px solid #f39c12' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#f39c12' }} />
            Remark Already Exists
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255,255,255,0.85)' }}>
            This alert already has a remark (possibly added from the VTS dashboard):
          </Typography>
          <Box
            sx={{
              backgroundColor: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'rgba(255,255,255,0.8)' }}
            >
              {formatRemarkForDisplay(existingRemark)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#f39c12' }}>
            Do you want to <strong>override</strong> it with your new comment?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#2C3E50', p: 2, gap: 1 }}>
          <Button
            onClick={() => setOverrideDialogOpen(false)}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Keep Existing
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            sx={{ backgroundColor: '#f39c12', '&:hover': { backgroundColor: '#e67e22' } }}
          >
            Yes, Override
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleAlertMapModal;
